import axios from 'axios';
import { ADMIN_API_URL, ADMIN_AUTH_TOKEN } from '../constants';

/**
 * Customer Sign-In: Generate dynamic auth token for a customer
 * @param {string} mobile - Customer's mobile number
 * @returns {Promise<Object>} - { success, token, customer, message }
 */
export const customerSignIn = async (mobile) => {
  try {
    console.log('[leadService] Signing in customer with mobile:', mobile);

    const response = await axios.post(
      `${ADMIN_API_URL}customer/sign-in`,
      { mobile },
      {
        headers: {
          'Content-Type': 'application/json',
        }
      }
    );

    if (response.data && response.data.status === 200) {
      console.log('[leadService] Customer sign-in successful');
      return {
        success: true,
        token: response.data.data.token,
        customer: response.data.data.customer,
        message: response.data.message
      };
    }

    return {
      success: false,
      token: null,
      customer: null,
      message: response.data?.message || 'Sign-in failed'
    };
  } catch (error) {
    console.error('[leadService] Customer sign-in error:', error);
    
    // Extract error message - handle nested error objects
    const extractErrorMessage = (err, defaultMsg) => {
      if (err.response?.data?.message) {
        const msg = err.response.data.message;
        return typeof msg === 'object' ? (msg.message || JSON.stringify(msg)) : msg;
      }
      if (err.message) return err.message;
      return defaultMsg;
    };
    
    // Handle 404 - No lead found (new customer)
    if (error.response?.status === 404) {
      return {
        success: false,
        isNewCustomer: true,
        token: null,
        customer: null,
        message: extractErrorMessage(error, 'No lead found for this mobile number')
      };
    }

    return {
      success: false,
      token: null,
      customer: null,
      message: extractErrorMessage(error, 'Failed to sign in')
    };
  }
};

/**
 * Fetch all leads for a given mobile number
 * @param {string} mobile - Customer's mobile number
 * @param {string} authToken - Authorization token (optional, defaults to env token)
 * @returns {Promise<Object>} - { status, message, data: { leads, count } }
 */
export const fetchLeadsByMobile = async (mobile, authToken = null, didRetry = false) => {
  // Deduplicate concurrent fetches per (mobile, token) key
  const tokenFromCaller = authToken || null;
  // Lazily initialize inflight map on module/global to persist across HMR
  if (!global.__lead_fetch_inflight__) {
    global.__lead_fetch_inflight__ = new Map();
  }
  const inFlight = global.__lead_fetch_inflight__;

  const ensureToken = async () => {
    if (tokenFromCaller) return tokenFromCaller;
    // No token provided - generate from customerSignIn
    try {
      const signInResult = await customerSignIn(mobile);
      if (signInResult.success && signInResult.token) {
        return signInResult.token;
      }
      // If sign-in indicates new customer, return null so caller can treat as no leads
      if (signInResult.isNewCustomer) return null;
      // otherwise throw to be handled downstream
      throw new Error(signInResult.message || 'Failed to obtain token via sign-in');
    } catch (err) {
      throw err;
    }
  };

  const handleFetch = async () => {
    let token;
    try {
      token = await ensureToken();
    } catch (err) {
      console.error('[leadService] Failed to obtain token before fetching leads:', err);
      return { success: false, data: [], count: 0, message: err.message || 'Failed to obtain token' };
    }

    const compositeKey = `${mobile}::${token || 'no-token'}`;
    if (inFlight.has(compositeKey)) {
      return inFlight.get(compositeKey);
    }

    const promise = (async () => {
      try {
        const headers = { 'Content-Type': 'application/json' };
        if (token) headers['authorization'] = token;

        console.log('[leadService] Fetching leads for mobile:', mobile);
        console.log('[leadService] Using token:', token ? 'Yes (length: ' + token.length + ')' : 'No token');

        const response = await axios.post(
          `${ADMIN_API_URL}fetch-lead-data`,
          {
            mobile: mobile,
            minLimit: 0,
            maxLimit: 100
          },
          { headers }
        );

        return {
          success: true,
          data: response.data.data || [],
          count: Array.isArray(response.data.data) ? response.data.data.length : (response.data.data ? 1 : 0),
          totalRecords: response.data.p?.totalRecords || 0,
          message: response.data.message
        };
      } catch (error) {
        console.error('Error fetching leads:', error);
        // If token expired (admin returned JWT TokenExpiredError), try refreshing once
        const errMsgObj = error.response?.data?.message;
        const isTokenExpired = errMsgObj && (errMsgObj.name === 'TokenExpiredError' || (typeof errMsgObj === 'string' && errMsgObj.toLowerCase().includes('jwt expired')));
        if (isTokenExpired && !didRetry) {
          try {
            console.log('[leadService] Detected expired admin token - attempting customer sign-in to refresh token for mobile:', mobile);
            const signInResult = await customerSignIn(mobile);
            if (signInResult.success && signInResult.token) {
              console.log('[leadService] Obtained refreshed token from sign-in, retrying fetch-lead-data');
              const retryResult = await fetchLeadsByMobile(mobile, signInResult.token, true);
              return { ...retryResult, newToken: signInResult.token };
            }
          } catch (refreshErr) {
            console.error('[leadService] Failed to refresh admin token via customerSignIn:', refreshErr);
          }
        }

        // Extract error message - handle nested error objects
        let errorMessage = 'Failed to fetch leads';
        if (error.response?.data?.message) {
          const msg = error.response.data.message;
          errorMessage = typeof msg === 'object' ? (msg.message || JSON.stringify(msg)) : msg;
        } else if (error.message) {
          errorMessage = error.message;
        }

        return {
          success: false,
          data: [],
          count: 0,
          totalRecords: 0,
          message: errorMessage
        };
      } finally {
        // cleanup in-flight entry
        inFlight.delete(compositeKey);
      }
    })();

    inFlight.set(compositeKey, promise);
    return promise;
  };

  return handleFetch();
};

/**
 * Check if customer is new or returning (has existing leads)
 * @param {string} mobile - Customer's mobile number
 * @param {string} authToken - Authorization token (optional)
 * @returns {Promise<Object>} - { isNewCustomer: boolean, leadCount: number }
 */
export const checkCustomerStatus = async (mobile, authToken = null) => {
  const result = await fetchLeadsByMobile(mobile, authToken);
  return {
    isNewCustomer: result.count === 0,
    leadCount: result.count,
    leads: result.data
  };
};

